import { useEffect, useMemo, useState } from 'react';
import { X, Download, Printer, Loader2, Tag } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { getCurrentUserOrganizationId } from '../../../lib/stores/referenceDataCache';
import { useLabelStore } from '../../../lib/stores/labelStore';
import { generateQrPngDataUrl } from '../../../lib/utils/qrDataUrl';
import { buildProductPublicUrl } from '../../../lib/utils/productPublicUrl';
import { formatWarehouseName } from '../../../lib/utils/warehouseLabel';
import { formatQuantity } from '../../../lib/utils/quantityFormat';
import type { OrderItem } from '../../../lib/types/orders';
import {
  STANDARD_LABEL_PAGE, labelPageOf, buildOperationalLabelsPdf, printPdfDocument,
  type OperationalLabelUse, type OperationalLabelContent,
} from '../../../lib/warehouse/operationalLabel';

/**
 * Etichette QR dall'ordine cliente (richiesta Say Farm): per gli articoli
 * dell'ordine si sceglie quali stampare e quante copie, senza aprire le schede
 * prodotto. Stesso disegno e stesso formato stampante dell'etichetta della
 * scheda articolo (lib/warehouse/operationalLabel.ts): Interno = nome magazzino
 * e magazzino, Ordine = nome commerciale; Lotto Campo e QR della pagina
 * pubblica del prodotto (tracciabilità e documenti) quando disponibili.
 *
 * Visibile solo con «Informazioni operative al posto del codice a barre»
 * (warehouse_settings.product_label_operational).
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  items: OrderItem[];
  /** Confezioni per riga d'ordine (confezionamento dell'offerta): copie proposte */
  packCountByOrderItem?: Record<string, number>;
}

interface ProductInfo {
  id: string;
  name: string;
  sku: string | null;
  commercial_name: string | null;
  field_lot: string | null;
  public_page_enabled: boolean | null;
  public_page_token: string | null;
}

interface Row {
  key: string;
  item: OrderItem;
  selected: boolean;
  copies: number;
  zoneId: string;
}

export function OrderLabelsModal({ isOpen, onClose, orderNumber, items, packCountByOrderItem }: Props) {
  const [use, setUse] = useState<OperationalLabelUse>('order');
  const [rows, setRows] = useState<Row[]>([]);
  const [products, setProducts] = useState<Record<string, ProductInfo>>({});
  const [qrByProduct, setQrByProduct] = useState<Record<string, string>>({});
  const [zones, setZones] = useState<Array<{ id: string; name: string; code: string | null }>>([]);
  const [loading, setLoading] = useState(false);

  const { printerConfigs, fetchPrinterConfigs } = useLabelStore();
  const [printerId, setPrinterId] = useState<string | null>(null);
  useEffect(() => { if (isOpen) fetchPrinterConfigs(); }, [isOpen, fetchPrinterConfigs]);
  useEffect(() => {
    if (printerId === null && printerConfigs.length > 0) {
      setPrinterId((printerConfigs.find(p => p.is_default) || printerConfigs[0]).id);
    }
  }, [printerConfigs, printerId]);
  const printer = printerConfigs.find(p => p.id === printerId);
  const page = labelPageOf(printer);

  const productIds = useMemo(
    () => [...new Set(items.map(i => i.product_id).filter(Boolean))].sort(),
    [items],
  );
  // Chiavi stabili: un nuovo array dal genitore con le stesse righe non deve
  // ricaricare e cancellare le scelte fatte nella finestra
  const itemsKey = items.map(i => `${i.id}:${i.product_id}:${i.quantity}`).join('|');
  const packKey = JSON.stringify(packCountByOrderItem || {});

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const orgId = await getCurrentUserOrganizationId();
      const [prodRes, zoneRes, storageRes] = await Promise.all([
        productIds.length
          ? supabase.from('products')
              .select('id, name, sku, commercial_name, field_lot, public_page_enabled, public_page_token')
              .in('id', productIds)
          : Promise.resolve({ data: [] }),
        orgId
          ? supabase.from('warehouse_zones').select('id, name, code').eq('organization_id', orgId).eq('is_active', true)
          : Promise.resolve({ data: [] }),
        productIds.length
          ? supabase.from('product_zone_storage').select('product_id, zone_id, quantity').in('product_id', productIds)
          : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;

      const byId: Record<string, ProductInfo> = {};
      for (const p of (prodRes.data || []) as ProductInfo[]) byId[p.id] = p;
      setProducts(byId);
      setZones(((zoneRes.data || []) as Array<{ id: string; name: string; code: string | null }>)
        .sort((a, b) => formatWarehouseName(a).localeCompare(formatWarehouseName(b), 'it')));

      // Magazzino proposto per l'uso interno: quello dove l'articolo ha più giacenza
      const bestZone: Record<string, { zone: string; qty: number }> = {};
      for (const s of (storageRes.data || []) as Array<{ product_id: string; zone_id: string; quantity: number | null }>) {
        const q = Number(s.quantity || 0);
        if (!bestZone[s.product_id] || q > bestZone[s.product_id].qty) bestZone[s.product_id] = { zone: s.zone_id, qty: q };
      }
      setRows(items.map((item, i) => ({
        key: item.id || `${item.product_id}-${i}`,
        item,
        selected: true,
        // Una per confezione se l'offerta indica il confezionamento, altrimenti una
        copies: Math.min(100, Math.max(1, (item.id && packCountByOrderItem?.[item.id]) || 1)),
        zoneId: bestZone[item.product_id]?.zone || '',
      })));

      // QR della pagina pubblica, uno per articolo
      const qr: Record<string, string> = {};
      await Promise.all(Object.values(byId).map(async p => {
        if (!p.public_page_enabled || !p.public_page_token) return;
        try { qr[p.id] = await generateQrPngDataUrl(buildProductPublicUrl(p.public_page_token)); }
        catch (err) { console.error('Error generating QR:', err); }
      }));
      if (!cancelled) { setQrByProduct(qr); setLoading(false); }
    })().catch(err => { console.error('[OrderLabelsModal]', err); if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemsKey, packKey]);

  const update = (key: string, patch: Partial<Row>) =>
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const selected = rows.filter(r => r.selected && r.copies > 0);
  const totalLabels = selected.reduce((n, r) => n + r.copies, 0);
  const allSelected = rows.length > 0 && rows.every(r => r.selected);

  const buildDoc = () => buildOperationalLabelsPdf(page, selected.map(r => {
    const p = products[r.item.product_id];
    const zone = zones.find(z => z.id === r.zoneId);
    const content: OperationalLabelContent = {
      use,
      productName: p?.name || r.item.product_name,
      commercialName: p?.commercial_name,
      warehouse: zone ? formatWarehouseName(zone) : '',
      fieldLot: p?.field_lot,
      qrDataUrl: qrByProduct[r.item.product_id] || null,
    };
    return { content, copies: r.copies };
  }));

  const handleDownload = () => {
    buildDoc().save(`Etichette-${use === 'internal' ? 'interne' : 'ordine'}-${orderNumber.replace(/[^a-zA-Z0-9-_]/g, '_')}.pdf`);
  };

  if (!isOpen) return null;

  const inputCls = 'px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Tag className="w-5 h-5" /> Etichette dell&apos;ordine
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{orderNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Chiudi">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Uso dell&apos;etichetta</label>
              <div className="grid grid-cols-2 gap-2">
                {([['order', 'Ordine', 'Nome commerciale'], ['internal', 'Interno', 'Nome magazzino']] as const).map(([value, title, hint]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setUse(value)}
                    className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                      use === value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">{title}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{hint}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Stampante e formato</label>
              <select value={printerId ?? ''} onChange={e => setPrinterId(e.target.value)} className={`w-full py-2 ${inputCls}`}>
                <option value="">Standard ({STANDARD_LABEL_PAGE.w}×{STANDARD_LABEL_PAGE.h} mm)</option>
                {printerConfigs.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({Number(p.paper_width_mm)}×{Number(p.paper_height_mm)} mm{p.orientation === 'landscape' ? ', orizzontale' : ''})</option>
                ))}
              </select>
              {printerConfigs.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Per la Brother QL-810W aggiungi la stampante in Magazzino → Etichette &amp; template → Stampanti.
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Articoli</label>
              <button
                type="button"
                onClick={() => setRows(prev => prev.map(r => ({ ...r, selected: !allSelected })))}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </button>
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Carico gli articoli…
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map(r => {
                  const p = products[r.item.product_id];
                  const commercial = p?.commercial_name?.trim();
                  const hasQr = !!qrByProduct[r.item.product_id];
                  return (
                    <div key={r.key} className={`flex items-start gap-3 px-3 py-2.5 ${r.selected ? '' : 'opacity-60'}`}>
                      <input
                        type="checkbox"
                        checked={r.selected}
                        onChange={e => update(r.key, { selected: e.target.checked })}
                        className="mt-1"
                        aria-label={`Stampa etichette per ${r.item.product_name}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{p?.name || r.item.product_name}</p>
                        {commercial && commercial !== (p?.name || r.item.product_name) && (
                          <p className="text-xs text-gray-700 dark:text-gray-200"><span className="text-gray-500 dark:text-gray-400">Nome commerciale:</span> {commercial}</p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {(p?.sku || r.item.product_sku) && <span>{p?.sku || r.item.product_sku} · </span>}
                          {formatQuantity(r.item.quantity, { unit: r.item.sale_unit || r.item.product?.unit_of_measure_1 || null })}
                          {p?.field_lot?.trim() ? ` · Lotto campo ${p.field_lot.trim()}` : ''}
                        </p>
                        {!hasQr && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Pagina pubblica non attiva: l&apos;etichetta esce senza QR</p>
                        )}
                        {use === 'internal' && r.selected && (
                          <select
                            value={r.zoneId}
                            onChange={e => update(r.key, { zoneId: e.target.value })}
                            className={`mt-1.5 w-full text-xs ${inputCls}`}
                            aria-label="Magazzino"
                          >
                            <option value="">{zones.length ? 'Magazzino non indicato' : 'Nessun magazzino configurato'}</option>
                            {zones.map(z => <option key={z.id} value={z.id}>{formatWarehouseName(z)}</option>)}
                          </select>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <input
                          type="number"
                          min={1}
                          max={100}
                          value={r.copies}
                          disabled={!r.selected}
                          onChange={e => update(r.key, { copies: Math.min(100, Math.max(1, parseInt(e.target.value) || 1)) })}
                          className={`w-16 text-center ${inputCls}`}
                          aria-label="Numero di etichette"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">etichette</p>
                      </div>
                    </div>
                  );
                })}
                {rows.length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Nessun articolo nell&apos;ordine.</p>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Formato {page.w}×{page.h} mm{printer ? ` · ${printer.name}` : ''}. Le copie proposte seguono il confezionamento dell&apos;offerta, se indicato.
            </p>
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <span className="mr-auto text-sm text-gray-600 dark:text-gray-300">
            {totalLabels} {totalLabels === 1 ? 'etichetta' : 'etichette'}
          </span>
          <button onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
            Annulla
          </button>
          <button
            onClick={() => printPdfDocument(buildDoc())}
            disabled={loading || totalLabels === 0}
            className="px-4 py-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Printer className="w-4 h-4 mr-2" /> Stampa
          </button>
          <button
            onClick={handleDownload}
            disabled={loading || totalLabels === 0}
            className="px-4 py-2 bg-[#0f6cbd] text-white rounded-lg hover:bg-[#0a5ba8] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Download className="w-4 h-4 mr-2" /> Scarica PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderLabelsModal;
